import React, { PureComponent } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import { Paper, Popover, TextField, Box, Drawer, Slide, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FaRegHandScissors } from 'react-icons/fa';
import { LiaDotCircleSolid } from 'react-icons/lia';
import { MdOutlineAutoFixHigh } from 'react-icons/md';
import { RiLock2Fill } from 'react-icons/ri';
import { LuLasso, LuSparkles } from 'react-icons/lu';
import { FaCircle } from 'react-icons/fa6';
import MapButton from './MapButton';
import { ImListNumbered, ImMagicWand, ImSortNumericAsc, ImTab } from 'react-icons/im';
import { FaNfcDirectional } from 'react-icons/fa6';
import { GiEncirclement } from 'react-icons/gi';
import { IoLayersOutline } from 'react-icons/io5';
import { MdOutlineBorderVertical } from 'react-icons/md';
import { BiPolygon } from 'react-icons/bi';
import { PiRectangle } from 'react-icons/pi';
import { FaDrawPolygon } from 'react-icons/fa';
import { GiHazardSign, GiPathDistance, GiStonePath } from 'react-icons/gi';
import { AiOutlineClose } from 'react-icons/ai';
import { FaEraser } from 'react-icons/fa';
import { IoMdAddCircleOutline } from 'react-icons/io';
import { FcCheckmark } from 'react-icons/fc';
import { DRAW_TYPES, SampleSource, SelectCandidate, SettingsAccessLevel, TakenCore } from '../../../types/types';
import './styles/drawbutton.css';
import { MAP_TYPES, MapType } from '../helpers/mapTypes';
import { getCurrentMission } from '../../../dataModelHelpers';
import { TiChartLineOutline } from 'react-icons/ti';
import { calculateTakenCoreCentroid, cleanSet, nextPaint, updateState } from '../../../utils';
import { transformTakenCoresToArmPosition } from '../../../services/RobotArmService';
import { Coordinate } from 'ol/coordinate';
import { DrawConfig } from '../../../types/types';
import { dispatchMissionUpdated } from '../../../missionEvents';
import { dispatchCoresNeedUpdate, dispatchSamplesUpdated } from '../../../mapEvents';
import MapLoadingButton from './MapLoadingButton';
import CorePlacementTuningPanel from './CorePlacementTuningPanel';
import { GiNuclearBomb } from 'react-icons/gi';
import { alertConfirm, alertError, alertInfo, alertSuccess, alertWarn } from '../../../alertDispatcher';
import { MdNotInterested } from 'react-icons/md';
import { ShowChangeTypeSelectorPopup, promptForChangeReason } from '../../../changeReasonHelpers';
import { FertilityArmOffset, HDArmOffset, RobotArmOffsetX, RobotArmOffsetY } from '../../../db/local_storage';
import SmartPathForm from './SmartPathForm';
import MapLayersForm from '../sidebar/MapLayersForm';
import { SoilCore } from '../../../db';

interface DrawButtonProps {
  addSample: (point: Coordinate, sampleId: string, sampleSource: SampleSource) => Promise<void>;
  addCore: (point: Coordinate, sampleId: string, sampleSource: SampleSource) => Promise<SoilCore | undefined>;
  addSampleActive: Coordinate | null;
  addSampleAfterPoint: (point: Coordinate, sampleId: string, sampleSource: SampleSource) => Promise<void>;
  cancelAddSample: () => void;
  coreLine: Coordinate[];
  drawConfig: DrawConfig;
  mapType: MapType;
  renumber: boolean;
  reorderLine: Coordinate[];
  makeContainedPolygonsHoles: () => void;
  fixPolygons: () => void;
  toggleDrawCores: () => void;
  deleteAllCoresOrSamples: () => void;
  toggleDrawGenericZone: () => void;
  toggleDrawLassoZone: () => void;
  toggleDrawFieldZone: () => void;
  toggleDrawSampleZone: () => void;
  toggleDrawToAddPolyToZone: (zoneId: number) => void;
  toggleDrawReorder: () => void;
  toggleDrawSample: (action: 'Moving' | 'Adding') => Promise<boolean>;
  togglePlaceParkingPoint: () => void;
  togglePlaceStartingPoint: () => void;
  togglePlaceEndPoint: () => void;
  togglePlacePullinPoint: () => void;
  toggleDrawSlowZone: () => void;
  toggleDrawUnsafeZone: () => void;
  toggleEraser: () => void;
  toggleCutter: () => void;
  updateRenumber: (renumber: boolean) => void;
  zoneMission: boolean;
  addCheckpoint: (name: string) => void;
  enterNewSampleId: boolean;
  accessLevel: SettingsAccessLevel;
  renameSampleId: boolean;
  renameSample: (newSampleId: string) => Promise<void>;
  isFieldSelectable: boolean;
  toggleIsFieldSelectable: () => void;
  zoneTypeChangeCandidate: SelectCandidate | null;
  popperOffset?: [number, number]; // Optional offset for the Popper panel
}

interface DrawButtonState {
  containerOpen: boolean;
  anchorEl: HTMLElement | null;
  sampleText: string;
  sampleError: string | null;
  drawButtonAnchor: HTMLButtonElement | null;
  smartPathDrawerOpen: boolean;
  mapLayersDrawerOpen: boolean;
  corePanelOpen: boolean;
}

export default class DrawButton extends PureComponent<DrawButtonProps, DrawButtonState> {
  SERIALIZABLE_STATE_VARIABLES: (keyof DrawButtonState)[] = ['containerOpen'];
  buttonRef: React.RefObject<unknown>;

  constructor(props: DrawButtonProps) {
    super(props);

    this.state = {
      containerOpen: false,
      anchorEl: null,
      sampleText: '',
      sampleError: null,
      drawButtonAnchor: null,
      smartPathDrawerOpen: false,
      mapLayersDrawerOpen: false,
      corePanelOpen: false,
    };

    this.toggleContainer = this.toggleContainer.bind(this);
    // TODO add sample auto-number
    this.handleApplySample = this.handleApplySample.bind(this);
    this.handleStartHover = this.handleStartHover.bind(this);
    this.handleStopHover = this.handleStopHover.bind(this);
    this.toggleRenumber = this.toggleRenumber.bind(this);

    this.buttonRef = React.createRef();
  }

  componentDidMount() {
    this.deserializeState();

    const anchorEl = document.getElementById('toggleDrawUnsafeZone');
    this.setState({ anchorEl });
  }

  componentWillUnmount() {
    this.serializeState();
  }

  /**
   * serialize the state into session
   */
  serializeState(this: DrawButton) {
    const serializedState: Partial<DrawButtonState> = {};

    for (const stateKey of this.SERIALIZABLE_STATE_VARIABLES) {
      const value = this.state[stateKey];
      serializedState[stateKey] = value as never; // this is not ideal, why is this acting like this?
    }

    localStorage.setItem('DrawButtonState', JSON.stringify(serializedState));
  }

  /**
   * deserialize the state
   */
  deserializeState() {
    const serializedState = JSON.parse(localStorage.getItem('DrawButtonState') ?? '{}');

    if (serializedState) {
      for (const stateKey of Object.keys(serializedState)) {
        this.setState(updateState(stateKey as keyof DrawButtonState, serializedState[stateKey]));
      }
    }
  }

  toggleContainer(event) {
    this.setState({ containerOpen: !this.state.containerOpen });
  }

  handleStartHover(event: React.MouseEvent<HTMLButtonElement>) {
    if (!this.state.drawButtonAnchor) {
      this.setState({ drawButtonAnchor: event.currentTarget });
    }
  }

  handleStopHover() {
    this.setState({ drawButtonAnchor: null });
  }

  toggleRenumber() {
    this.props.updateRenumber(!this.props.renumber);
  }

  validateSample = () => {
    const mission = getCurrentMission();
    let validationMap = { error: false, text: '' };

    if (this.props.zoneMission) {
      return validationMap;
    }

    if (!this.state.sampleText || (!this.props.zoneMission && !this.state.sampleText.match(/^[a-zA-Z0-9]+$/))) {
      validationMap = { error: true, text: 'Must be alphanumeric' };
    }

    const samples = mission?.getAllSamples() ?? [];

    if (samples.find((sample) => sample.sample_id === this.state.sampleText)) {
      if (this.props.accessLevel === 'Technician' && this.props.renameSampleId) {
        return validationMap;
      }

      validationMap = { error: true, text: 'Sample ID already exists' };
    }

    return validationMap;
  };

  // TODO add sample auto-number
  async handleApplySample() {
    const validationMap = this.validateSample();

    if (!validationMap.error && this.props.addSampleActive) {
      if (this.props.mapType === MAP_TYPES.MAPS) {
        if (this.props.renameSampleId) {
          await this.props.renameSample(this.state.sampleText);
        } else if (this.props.zoneMission) {
          try {
            const newCore = await this.props.addCore(
              this.props.addSampleActive,
              this.state.sampleText,
              'Rogo Map Maker',
            );

            if (!newCore) {
              return;
            }
          } catch (error) {
            console.error('Error adding core:', error);

            return;
          }
        } else {
          await this.props.addSample(this.props.addSampleActive, this.state.sampleText, 'Rogo Map Maker');
        }
      } else if (this.props.mapType === MAP_TYPES.SAMPLING) {
        await this.props.addSampleAfterPoint(this.props.addSampleActive, this.state.sampleText, 'Rogo Field Ops');
      }

      this.setState({ sampleText: '' });

      this.props.cancelAddSample();
    } else {
      this.setState({ sampleError: validationMap.text });
    }
  }

  handleAutoPlaceCores = async (): Promise<void> => {
    // Technicians get the tuning panel instead of running immediately; regular
    // users run placement with the baked defaults (current behavior).
    if (this.props.accessLevel === 'Technician') {
      this.setState({ corePanelOpen: true });

      return;
    }

    const mission = getCurrentMission();

    if (!mission) {
      alertError('No mission loaded');

      return;
    }

    const spec = mission.getPrimarySpec();

    if (!spec || spec.cores <= 0) {
      alertError(`Auto-place requires cores per sample > 0 (got ${spec?.cores ?? 'none'})`);

      return;
    }

    const sampleZones = cleanSet(mission.getZones().getSampleZones());

    if (sampleZones.length === 0) {
      alertError('No sample zones in this mission');

      return;
    }

    // Validate boundaries.
    const zonesWithInvalidBoundaries = sampleZones.filter((sz) => {
      const z = sz.getZone();

      return z && !z.hasValidBoundaries();
    });

    if (zonesWithInvalidBoundaries.length > 0) {
      alertWarn(
        `${zonesWithInvalidBoundaries.length} sample zone(s) have invalid boundaries. Run 'Fix Polygons' first.`,
      );

      return;
    }

    // Pulled-core refusal.
    const hasPulled = sampleZones.some((sz) => {
      const samples = sz.getSampleSite()?.getSamples() ?? [];

      return cleanSet(samples).some((s) => cleanSet(s.getSoilCores()).some((c) => c.pulled));
    });

    if (hasPulled) {
      alertError('Cannot auto-place: one or more zones have pulled cores. Auto-place is for unsampled missions only.');

      return;
    }

    // Confirm-then-wipe if any zone already has cores.
    const zonesWithCores = sampleZones.filter((sz) => {
      const samples = sz.getSampleSite()?.getSamples() ?? [];

      return cleanSet(samples).some((s) => cleanSet(s.getSoilCores()).length > 0);
    });

    if (zonesWithCores.length > 0) {
      const confirmed = await alertConfirm(`Replace existing cores in ${zonesWithCores.length} zone(s)?`);

      if (!confirmed) {
        return;
      }
    }

    this.props.addCheckpoint(`Auto-place cores in ${sampleZones.length} zone(s)`);

    // Let the button's loading spinner paint before the heavy synchronous
    // placement work (the kernel blocks the main thread for several seconds).
    await nextPaint();

    // Wipe pending cores across all sample-zone samples.
    for (const sz of sampleZones) {
      const samples = sz.getSampleSite()?.getSamples() ?? [];

      for (const s of cleanSet(samples)) {
        for (const c of cleanSet(s.getSoilCores())) {
          c.dispose();
        }
      }
    }

    let result: Awaited<ReturnType<typeof mission.autoPlaceCores>>;

    try {
      result = await mission.autoPlaceCores();
    } catch (e) {
      alertError(`Auto-place failed: ${e instanceof Error ? e.message : 'unknown error'}`);

      return;
    }

    // calculate_path_restore_cores (not calculate_path) is required here:
    // with no waypoints yet, it builds the cores list from pending cores sorted
    // by (sample.order, core_number), which matches the greedy order we just set.
    // Plain calculate_path with default args would pass an empty cores array and
    // produce no waypoints.
    await mission.calculate_path_restore_cores();
    dispatchMissionUpdated();
    dispatchCoresNeedUpdate();

    const placedZones = sampleZones.length - result.skippedZones.length;
    const skippedMsg =
      result.skippedZones.length > 0
        ? `; skipped ${result.skippedZones.length} zone(s) (too small after offsets): ${result.skippedZones.join(', ')}`
        : '';
    const segmentsMsg = result.skippedSegments > 0 ? ` (${result.skippedSegments} segment(s) skipped)` : '';

    alertSuccess(`Placed ${result.placed} core(s) across ${placedZones} zone(s)${segmentsMsg}${skippedMsg}`);
    alertInfo('This is NOT a Smart Path. Use Smart Path for optimal routing.');
  };

  render() {
    const mission = getCurrentMission();

    if (!mission) {
      return null;
    }

    return (
      <React.Fragment>
        {this.props.zoneMission && (
          <CorePlacementTuningPanel
            accessLevel={this.props.accessLevel}
            open={this.state.corePanelOpen}
            onClose={() => this.setState({ corePanelOpen: false })}
            addCheckpoint={this.props.addCheckpoint}
          />
        )}
        <MapButton
          onClick={this.toggleContainer}
          toggled={this.state.containerOpen}
          id={'toggleDrawUnsafeZone'}
          tooltip="Draw Map Items"
        >
          <FaDrawPolygon size={20} />
        </MapButton>
        {this.state.containerOpen && (
          <Paper
            style={{
              position: 'fixed',
              left: 10 + (this.props.popperOffset?.[0] ?? 0),
              top: 140 + (this.props.popperOffset?.[1] ?? 0),
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxHeight: 'calc(100vh - 160px)',
              overflowY: 'auto',
            }}
          >
            <Box
              style={{
                width: '100%',
                backgroundColor: 'rgba(120, 180, 255, 0.12)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              {this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={async () => {
                    this.setState((prev) => ({
                      ...prev,
                      mapLayersDrawerOpen: !prev.mapLayersDrawerOpen,
                    }));
                  }}
                  tooltip="Map Layers"
                >
                  <IoLayersOutline size={24} />
                </MapButton>
              )}

              <MapButton
                onClick={() => this.props.toggleIsFieldSelectable()}
                style={{
                  backgroundColor: this.props.isFieldSelectable ? '#adf5e5ff' : undefined,
                }}
                tooltip="Toggle Field Selectable"
              >
                <RiLock2Fill size={24} color="rgb(255, 165, 5)" />
              </MapButton>
            </Box>

            <Box
              style={{
                width: '100%',
                backgroundColor: 'rgba(255, 180, 200, 0.16)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              <MapButton
                onClick={this.props.toggleDrawUnsafeZone}
                style={{
                  backgroundColor: this.props.drawConfig.featureType === DRAW_TYPES.UNSAFE_ZONE ? '#e2e0e0' : undefined,
                }}
                tooltip="Draw Unsafe Zone (Crtl+1)"
              >
                <GiHazardSign size={24} color="rgb(164, 22, 35)" />
              </MapButton>

              <MapButton
                onClick={this.props.toggleDrawSlowZone}
                style={{
                  backgroundColor: this.props.drawConfig.featureType === DRAW_TYPES.SLOW_ZONE ? '#e2e0e0' : undefined,
                }}
                tooltip="Draw Slow Zone (Ctrl+2)"
              >
                <GiHazardSign size={24} color="rgb(141, 103, 8)" />
              </MapButton>

              <MapButton
                onClick={this.props.toggleDrawGenericZone}
                style={{
                  backgroundColor: this.props.drawConfig.featureType === DRAW_TYPES.PULLIN_ZONE ? '#e2e0e0' : undefined,
                }}
                tooltip="Draw Generic Zone (Ctrl+3)"
              >
                <BiPolygon size={24} color="rgb(29, 120, 29)" />
              </MapButton>

              {this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={this.props.toggleDrawFieldZone}
                  style={{
                    backgroundColor:
                      this.props.drawConfig.featureType === DRAW_TYPES.FIELD_ZONE ? '#e2e0e0' : undefined,
                  }}
                  tooltip="Draw Field Zone"
                >
                  <PiRectangle size={24} color="rgb(255, 165, 5)" />
                </MapButton>
              )}

              {this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={() => this.props.togglePlacePullinPoint()}
                  style={{
                    backgroundColor:
                      this.props.drawConfig.featureType === DRAW_TYPES.PULLIN_POINT ? '#e2e0e0' : undefined,
                  }}
                  tooltip="Set Pullin Point"
                  onMouseOver={this.handleStopHover}
                >
                  <FaCircle size={24} color="rgb(29, 120, 29)" />
                </MapButton>
              )}

              {this.props.zoneMission && this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={this.props.toggleDrawSampleZone}
                  style={{
                    backgroundColor:
                      this.props.drawConfig.featureType === DRAW_TYPES.FIELD_ZONE ? '#e2e0e0' : undefined,
                  }}
                  tooltip="Draw New Sample Zone"
                >
                  <FaNfcDirectional size={24} color="rgb(255, 2, 255)" />
                </MapButton>
              )}
            </Box>

            <Box
              style={{
                width: '100%',
                backgroundColor: 'rgba(180, 230, 230, 0.16)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              {this.props.zoneMission && this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={async () => {
                    const mission = getCurrentMission();

                    if (!mission) {
                      alertError('No mission loaded');

                      return;
                    }

                    this.props.addCheckpoint('Make field boundary a sample zone');

                    const { zone, errorMessages } = await mission.generateSampleZoneFromFieldBoundary();

                    if (!zone) {
                      alertError('Failed to make a field boundary a sample zone');
                    }

                    errorMessages.forEach((errorMessage) => {
                      alertWarn(errorMessage);
                    });
                  }}
                  tooltip="Make field boundary a sample zone"
                >
                  <GiEncirclement size={24} color="rgb(7, 85, 176)" />
                </MapButton>
              )}

              {this.props.zoneMission && this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={() => {
                    const mission = getCurrentMission();

                    if (!mission) {
                      alertError('No mission loaded');

                      return;
                    }

                    this.props.addCheckpoint('Match cores with zones');

                    mission.matchCoresWithZonesAndAlert();
                  }}
                  tooltip="Match cores with zones"
                >
                  <MdOutlineBorderVertical size={24} />
                </MapButton>
              )}

              {this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={() => {
                    this.props.toggleEraser();
                  }}
                  style={{
                    backgroundColor: this.props.drawConfig.featureType === DRAW_TYPES.ERASER ? '#e2e0e0' : undefined,
                  }}
                  tooltip="Erase Points (Draw 3 points: start, inBetween, end)"
                >
                  <FaEraser size={24} />
                </MapButton>
              )}

              {this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={() => {
                    this.props.toggleCutter();
                  }}
                  style={{
                    backgroundColor: this.props.drawConfig.featureType === DRAW_TYPES.CUTTER ? '#e2e0e0' : undefined,
                  }}
                  tooltip="Cut Polygon (Draw line across polygon to cut it)"
                >
                  <FaRegHandScissors size={24} />
                </MapButton>
              )}

              {this.props.mapType === MAP_TYPES.MAPS && (
                <MapButton
                  onClick={() => {
                    this.props.makeContainedPolygonsHoles();
                  }}
                  tooltip="Make contained polygons holes"
                >
                  <LiaDotCircleSolid size={24} />
                </MapButton>
              )}

              <MapButton
                onClick={() => {
                  this.props.fixPolygons();
                }}
                tooltip="Fix polygons"
              >
                <MdOutlineAutoFixHigh size={24} />
              </MapButton>
            </Box>

            <Box
              style={{
                width: '100%',
                backgroundColor: 'rgba(255, 210, 120, 0.18)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              <MapButton
                onClick={async () => {
                  this.setState((prev) => ({
                    ...prev,
                    smartPathDrawerOpen: !prev.smartPathDrawerOpen,
                  }));
                }}
                tooltip="Smart Path External"
              >
                <GiStonePath size={24} />
              </MapButton>

              <MapButton
                onClick={() => this.props.togglePlaceParkingPoint()}
                style={{
                  backgroundColor:
                    this.props.drawConfig.featureType === DRAW_TYPES.PARKING_POINT ? '#e2e0e0' : undefined,
                }}
                onMouseOver={this.handleStopHover}
                tooltip="Place Parking Point"
              >
                <img
                  src="./images/parking-point.png"
                  alt="Parking"
                  style={{ width: 24, height: 24, borderRadius: 4 }}
                />
              </MapButton>

              <MapButton
                onClick={() => this.props.togglePlaceStartingPoint()}
                style={{
                  backgroundColor:
                    this.props.drawConfig.featureType === DRAW_TYPES.STARTING_POINT ? '#e2e0e0' : undefined,
                }}
                onMouseOver={this.handleStopHover}
                tooltip="Place Starting Point"
              >
                <img
                  src="./images/starting-point.png"
                  alt="Starting Point"
                  style={{ width: 24, height: 24, borderRadius: 4 }}
                />
              </MapButton>

              <MapButton
                onClick={() => this.props.togglePlaceEndPoint()}
                style={{
                  backgroundColor: this.props.drawConfig.featureType === DRAW_TYPES.END_POINT ? '#e2e0e0' : undefined,
                }}
                onMouseOver={this.handleStopHover}
                tooltip="Place End Point"
              >
                <img src="./images/end-point.png" alt="End Point" style={{ width: 24, height: 24, borderRadius: 4 }} />
              </MapButton>

              <MapButton
                // onMouseOver={this.handleStartHover}
                onClick={() => {
                  this.props.toggleDrawReorder();
                }}
                style={{
                  backgroundColor:
                    this.props.drawConfig.featureType === DRAW_TYPES.REORDER_LINE ? '#e2e0e0' : undefined,
                }}
                disabled={this.props.coreLine.length > 0}
                tooltip="Reorder Path"
              >
                <GiPathDistance size={24} />
              </MapButton>

              <MapButton
                // onMouseOver={this.handleStartHover}
                onClick={async () => {
                  getCurrentMission()?.reverse_path();
                }}
                disabled={this.props.coreLine.length > 0}
                tooltip="Reverse Path"
              >
                <ImTab size={24} />
              </MapButton>
            </Box>

            <Box
              style={{
                width: '100%',
                backgroundColor: 'rgba(180, 220, 180, 0.16)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              {this.props.mapType === MAP_TYPES.MAPS && (
                <React.Fragment>
                  <MapButton
                    onClick={async () => await this.props.toggleDrawSample('Adding')}
                    style={{
                      backgroundColor:
                        this.props.drawConfig.featureType === DRAW_TYPES.CENTROID ? '#e2e0e0' : undefined,
                    }}
                    onMouseOver={this.handleStopHover}
                    tooltip={this.props.zoneMission ? 'Add Core' : 'Add Sample'}
                  >
                    <IoMdAddCircleOutline size={22} />
                  </MapButton>

                  {this.props.zoneMission && (
                    <MapButton
                      onClick={this.props.toggleDrawCores}
                      style={{
                        backgroundColor:
                          this.props.drawConfig.featureType === DRAW_TYPES.CORE_LINE ? '#e2e0e0' : undefined,
                      }}
                      disabled={this.props.reorderLine.length > 0}
                      tooltip="Draw Coring Stops"
                    >
                      <TiChartLineOutline size={24} />
                    </MapButton>
                  )}
                  {this.props.zoneMission && (
                    <MapLoadingButton
                      onClick={this.handleAutoPlaceCores}
                      disabled={this.props.reorderLine.length > 0}
                      tooltip="Auto-place Cores"
                    >
                      <LuSparkles size={22} />
                    </MapLoadingButton>
                  )}
                  {this.props.mapType === MAP_TYPES.MAPS && (
                    <MapButton
                      onClick={this.props.deleteAllCoresOrSamples}
                      disabled={this.props.reorderLine.length > 0}
                      tooltip={this.props.zoneMission ? 'Delete All Cores' : 'Delete All Samples'}
                    >
                      <GiNuclearBomb size={24} />
                    </MapButton>
                  )}
                </React.Fragment>
              )}

              {!this.props.zoneMission && (
                <MapButton
                  onClick={async () => {
                    const mission = getCurrentMission();
                    this.props.addCheckpoint(`Renumber Points`);
                    await mission?.renumber_samples(this.props.mapType === MAP_TYPES.SAMPLING, true);
                    dispatchMissionUpdated();
                    // renumber changes every sample_id — fall back to full core rebuild
                    dispatchCoresNeedUpdate();
                  }}
                  // onMouseLeave={this.handleStopHover}
                  toggled={this.props.renumber}
                  style={{ pointerEvents: 'auto' }}
                  tooltip="Renumber Points in Path Order"
                >
                  <ImSortNumericAsc size={22} />
                </MapButton>
              )}
              {!this.props.zoneMission && (
                <MapButton
                  onClick={async () => {
                    const mission = getCurrentMission();
                    this.props.addCheckpoint(`Reorder Path By Sample ID`);
                    const samples = mission?.getAllSamples() ?? [];
                    const sortedSamples = samples.sort((a, b) => {
                      return parseInt(a.sample_id) - parseInt(b.sample_id);
                    });
                    const coordinates = sortedSamples
                      .map((sample) => sample.getSampleSite()?.getSampleCentroid()?.getCoordinates())
                      .filter((coord) => !!coord);
                    await mission?.reorder(coordinates, false, this.props.mapType !== MAP_TYPES.MAPS);
                    dispatchMissionUpdated();
                    // reorder → calculate_path_restore_cores → calculate_path
                    // already fires dispatchCoresNeedUpdate() internally.
                  }}
                  // onMouseLeave={this.handleStopHover}
                  toggled={this.props.renumber}
                  style={{ pointerEvents: 'auto' }}
                  tooltip="Reorder Path By Sample ID"
                >
                  <ImListNumbered size={22} />
                </MapButton>
              )}
              {this.props.mapType === MAP_TYPES.MAPS && getCurrentMission()?.isLoamMission() && (
                <MapButton
                  onClick={async () => {
                    this.props.addCheckpoint(`The Magical Loam Job Transformer`);
                    const mission = getCurrentMission();

                    if (!mission) {
                      alertError('No mission loaded');

                      return;
                    }

                    const cores: TakenCore[] = mission.cores ? JSON.parse(mission.cores) : [];

                    const currentDx = RobotArmOffsetX.get();
                    const currentDy = RobotArmOffsetY.get();

                    const searchName = mission.name.toLowerCase();

                    if (searchName.includes('small plot')) {
                      if (searchName.includes('bd')) {
                        alertInfo('Using HD Mini Offset (Needs fixed, but does not matter for Loam Small Plot BD)');
                        RobotArmOffsetX.set(-1.1);
                        RobotArmOffsetY.set(-1.1);
                      } else {
                        alertInfo('Using Fertility Offset');
                        RobotArmOffsetX.set(FertilityArmOffset.dx);
                        RobotArmOffsetY.set(FertilityArmOffset.dy);
                      }
                    } else {
                      alertInfo('Using HD Offset');
                      RobotArmOffsetX.set(HDArmOffset.dx);
                      RobotArmOffsetY.set(HDArmOffset.dy);
                    }

                    transformTakenCoresToArmPosition(cores);

                    RobotArmOffsetX.set(currentDx);
                    RobotArmOffsetY.set(currentDy);

                    const coresBySample: Record<string, TakenCore[]> = {};
                    cores.forEach((core) => {
                      const sample_id = core[5];

                      if (!coresBySample[sample_id]) {
                        coresBySample[sample_id] = [];
                      }

                      coresBySample[sample_id].push(core);
                    });

                    const samples = mission.getAllSamples();
                    const missing_ids: string[] = [];
                    let samplesMoved = 0;
                    console.log(coresBySample);

                    for (const sample of samples) {
                      console.log(`Moving ${sample.sample_id}`);

                      if (coresBySample[sample.sample_id]) {
                        const centroid = calculateTakenCoreCentroid(coresBySample[sample.sample_id]);
                        await sample.getSampleSite().getSampleCentroid()?.move(centroid.x, centroid.y);
                        samplesMoved++;
                      } else {
                        missing_ids.push(sample.sample_id);
                      }
                    }

                    console.log(`Missing IDs: ${missing_ids.join(', ')}`);

                    const totalSamples = samples.length;

                    const percentageMoved = (samplesMoved / totalSamples) * 100;

                    if (percentageMoved === 100) {
                      alertSuccess(`Moved ${samplesMoved} / ${totalSamples} (${percentageMoved}%) samples`);
                    } else if (percentageMoved > 95) {
                      alertWarn(`Moved ${samplesMoved} / ${totalSamples} (${percentageMoved}%) samples`);
                    } else {
                      alertError(`Moved ${samplesMoved} / ${totalSamples} (${percentageMoved}%) of samples`);
                    }

                    await mission.clearSampleBarcodes();
                    mission.resetPulled();
                    mission.cores = '[]';

                    dispatchMissionUpdated();
                    // Magic wand moves centroids across many samples AND resets pulled state on every core — full rebuild.
                    dispatchCoresNeedUpdate();
                  }}
                  // onMouseLeave={this.handleStopHover}
                  toggled={this.props.renumber}
                  style={{ pointerEvents: 'auto' }}
                  tooltip="The Magical Loam Job Transformer"
                >
                  <ImMagicWand size={22} />
                </MapButton>
              )}
              <MapButton
                tooltip={'Skip / Unskip all unscanned'}
                key={'SkipAllUnscanned'}
                onClick={async () => {
                  const mission = getCurrentMission();

                  if (!mission) {
                    return;
                  }

                  const samples = mission?.getAllSamples();
                  const candidates = samples.filter((sample) => !sample.hasBarcode() && !sample.skipped_or_deleted);

                  if (candidates.length === 0) {
                    const skippedSamples = samples.filter((sample) => sample.skipped_or_deleted);

                    if (await alertConfirm(`Are you sure you want to unskip ${skippedSamples.length} samples?`)) {
                      this.props.addCheckpoint('Unskip All');
                      await mission.unskipAllSkipped();
                      dispatchMissionUpdated();
                      // unskipAllSkipped → calculate_path_restore_cores rebuilds
                      // every sample's cores. Targeted rebuild on the affected
                      // sample IDs so only those samples' JSX is regenerated.
                      dispatchSamplesUpdated(skippedSamples.map((s) => s.sample_id));
                    }
                  } else {
                    const selectorOptions: ShowChangeTypeSelectorPopup = {
                      sampleCoreOrZoneId: `${candidates.length} SAMPLES`,
                      changeTypeOptions: ['Skip', 'Delete', 'None'],
                      defaultChangeType: 'Skip',
                    };

                    const changeSampleResult = await promptForChangeReason(selectorOptions);

                    if (changeSampleResult.result === 'confirm' && !!changeSampleResult.selectedChangeType) {
                      this.props.addCheckpoint('Skip All Unscanned');
                      await mission.skipUnsampled(
                        changeSampleResult.selectedChangeType,
                        changeSampleResult.reason ?? '',
                      );
                      dispatchMissionUpdated();
                      // skipUnsampled disposes waypoints/cores on every unscanned
                      // sample — targeted rebuild on the candidates.
                      dispatchSamplesUpdated(candidates.map((s) => s.sample_id));
                    }
                  }
                }}
              >
                <MdNotInterested size={22} />
              </MapButton>

              {!this.props.zoneMission && (
                <MapButton
                  onClick={this.props.toggleDrawLassoZone}
                  style={{
                    backgroundColor:
                      this.props.drawConfig.featureType === DRAW_TYPES.LASSO_ZONE ? '#e2e0e0' : undefined,
                  }}
                  tooltip="Lasso Draw Mode"
                >
                  <LuLasso size={24} color="#c77e28" />
                </MapButton>
              )}
            </Box>

            <Box
              style={{
                width: '100%',
                backgroundColor: 'rgba(220, 220, 220, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '4px 0',
              }}
            >
              <MapButton
                onClick={() => this.setState({ containerOpen: false })}
                onMouseOver={this.handleStopHover}
                tooltip="Close Drawing Menu"
              >
                <AiOutlineClose size={24} />
              </MapButton>
            </Box>
          </Paper>
        )}
        <Popover
          anchorEl={this.state.anchorEl}
          open={
            Boolean(this.props.addSampleActive) && Boolean(this.props.enterNewSampleId || !!this.props.renameSampleId)
          }
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <TextField
            label={this.props.zoneMission && !this.props.renameSampleId ? '<Sample ID>.<Core #>' : 'Sample ID'}
            style={{ margin: 10 }}
            onChange={(event) => this.setState({ sampleText: event.target.value })}
            value={this.state.sampleText}
            size={'small'}
            error={Boolean(this.state.sampleError)}
            helperText={this.state.sampleError}
          />

          <MapLoadingButton style={{ marginTop: 15 }} onClick={this.handleApplySample}>
            <FcCheckmark size={24} />
          </MapLoadingButton>

          <MapButton style={{ marginTop: 18 }} onClick={this.props.cancelAddSample}>
            <AiOutlineClose size={22} />
          </MapButton>
        </Popover>
        <Drawer
          anchor="right"
          open={this.state.smartPathDrawerOpen}
          onClose={() => this.setState({ smartPathDrawerOpen: false })}
          variant="persistent"
          PaperProps={{
            style: {
              width: 360,
              borderRadius: 8,
              top: 140,
              height: 'auto',
              maxHeight: 'calc(100vh - 160px)',
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(4px)',
            },
          }}
          ModalProps={{
            keepMounted: true,
            disableEnforceFocus: true,
            hideBackdrop: true,
          }}
        >
          <Box
            p={2}
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
              style={{ flexShrink: 0 }} // Header doesn't shrink or scroll
            >
              <h3>Smart Path Settings</h3>
              <IconButton
                onClick={() => {
                  this.setState({ smartPathDrawerOpen: false });
                }}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box p={2} style={{ flex: 1, overflowY: 'auto' }}>
              {/* Form fills remaining height and scrolls */}
              <SmartPathForm
                addCheckpoint={this.props.addCheckpoint}
                mapType={this.props.mapType}
                onCompletion={async () => {
                  // this.setState({ smartPathModalOpen: false });
                }}
                mission={mission}
                accessLevel={this.props.accessLevel}
              />
            </Box>
          </Box>
        </Drawer>
        <Drawer
          anchor="right"
          open={this.state.mapLayersDrawerOpen}
          onClose={() => this.setState({ mapLayersDrawerOpen: false })}
          variant="persistent"
          PaperProps={{
            style: {
              width: 450,
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              top: 80,
            },
          }}
          ModalProps={{
            keepMounted: true,
            disableEnforceFocus: true,
            hideBackdrop: true,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
            <h3>Map Layers</h3>
            <IconButton
              onClick={() => {
                this.setState({ mapLayersDrawerOpen: false });
              }}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box p={2}>
            <MapLayersForm
              addCheckpoint={this.props.addCheckpoint}
              mapType={this.props.mapType}
              mission={mission}
              onAddPolyToZoneBtnClick={this.props.toggleDrawToAddPolyToZone}
              zoneTypeChangeCandidate={this.props.zoneTypeChangeCandidate}
            />
          </Box>
        </Drawer>
      </React.Fragment>
    );
  }
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<unknown, string> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});
